import { CheckIcon } from 'lucide-react-native';
import type { ReactNode } from 'react';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { Linking } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { Checkbox, CheckboxIcon, CheckboxIndicator, CheckboxLabel } from '@/components/ui/checkbox';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { LinkText } from '@/components/ui/link';
import { VStack } from '@/components/ui/vstack';
import { TERMS_OF_USE_URL } from '@/constants/links';

type ConfirmationVariant = 'pii' | 'phi' | 'research';

type ConfirmationCheckboxProps = {
  control?: Control<any>;
  errors?: FieldErrors<any>;
  fieldName?: string;
  isChecked?: boolean;
  onChange?: (checked: boolean) => void;
  isDisabled?: boolean;
  variant: ConfirmationVariant;
  className?: string;
};

/**
 * One source of truth for what each variant says, so the standalone and
 * form-controlled render paths below can never drift into showing different
 * copy for the same variant — they did, once, before this was pulled out.
 */
function confirmationContentFor(variant: ConfirmationVariant): {
  ariaLabel: string;
  testID: string;
  label: ReactNode;
} {
  if (variant === 'phi') {
    return {
      ariaLabel: 'No protected health information',
      testID: 'phi-confirmation-checkbox',
      label: (
        <>
          By uploading these attachments, I confirm these files do not contain protected health
          information*
        </>
      ),
    };
  }

  const termsLink = (
    <LinkText
      size="sm"
      className="text-primary-500 underline hover:text-primary-600"
      onPress={() => Linking.openURL(TERMS_OF_USE_URL)}>
      Terms of Use
    </LinkText>
  );

  /*
   * The research checkbox floats in the composer dock over the thread, so every
   * line it adds covers the empty state's examples on a phone. It says the same
   * two things in fewer words.
   */
  if (variant === 'research') {
    return {
      ariaLabel: 'No patient identifiable information',
      testID: 'research-confirmation-checkbox',
      label: (
        <>I confirm this has no patient-identifiable information and agree to the {termsLink}.*</>
      ),
    };
  }

  return {
    ariaLabel: 'No patient identifiable information',
    testID: 'pii-confirmation-checkbox',
    label: (
      <>
        By submitting this inquiry, I confirm this message does not contain patient-identifiable
        information and I agree to Primum's {termsLink}.*
      </>
    ),
  };
}

/**
 * Shared Confirmation Checkbox Component
 *
 * @overview
 * Reusable checkbox for PHI/PII confirmations with consistent styling and messaging.
 * Can be used as form-controlled (with react-hook-form) or standalone component.
 *
 * @behavior
 * - PII variant: Used in inquiry forms for general PII confirmation
 * - PHI variant: Used for file attachment PHI confirmation
 * - Research variant: gates the first send in a new research topic. Used
 *   standalone today (Ask the Library manages its input with `useState`, not
 *   react-hook-form), but the form-controlled path renders it correctly too.
 * - Consistent styling and concise messaging across both variants
 * - Form integration optional for standalone usage
 *
 * The validation message reads the controller's own `fieldState`, not the
 * `errors` object threaded in as a prop. Reading the prop left the message
 * unrendered in the running app even while the same rule correctly blocked
 * submission, so the user got a form that refused to submit and said nothing
 * about why. `errors` is still accepted, but only to decide whether the
 * checkbox is form-controlled — do not route error text back through it.
 */
export function ConfirmationCheckbox({
  control,
  errors,
  fieldName = 'confirmation',
  isChecked,
  onChange,
  isDisabled = false,
  variant,
  className = '',
}: ConfirmationCheckboxProps) {
  const isFormControlled = !!(control && errors);

  const { ariaLabel, testID, label } = confirmationContentFor(variant);

  /**
   * The research card sits on `background-50`. The base label color
   * (`text-typography-600`) clears AA only on `background-0` per
   * accessibility.md, so it needs bumping to `text-typography-700` here —
   * `data-[checked=true]:text-typography-900` is a distinct modifier and
   * still wins once checked.
   */
  const labelClassName = twJoin('-m-1 flex-1', variant === 'research' && 'text-typography-700');

  const content = (
    <Checkbox
      className={twJoin('items-start', 'justify-start gap-3', className)}
      value={isFormControlled ? '' : 'checked'}
      onChange={isFormControlled ? undefined : onChange}
      isChecked={isFormControlled ? undefined : isChecked}
      size="sm"
      isDisabled={isDisabled}
      aria-label={ariaLabel}
      testID={testID}>
      <CheckboxIndicator>
        <CheckboxIcon as={CheckIcon} />
      </CheckboxIndicator>

      <CheckboxLabel className={labelClassName}>{label}</CheckboxLabel>
    </Checkbox>
  );

  if (isFormControlled) {
    return (
      <Controller
        name={fieldName}
        control={control}
        render={({ field: { onChange: fieldOnChange, value }, fieldState, formState }) => {
          const showError = !!fieldState.error && (fieldState.isTouched || formState.isSubmitted);

          return (
            <FormControl
              className={twJoin(className, variant === 'phi' && 'web:mb-9')}
              isInvalid={showError}
              isDisabled={isDisabled}>
              <Checkbox
                className={twJoin('items-start', 'justify-start gap-3')}
                value={value?.toString() || ''}
                onChange={(checked) => fieldOnChange(checked)}
                isChecked={value}
                size="sm"
                aria-label={ariaLabel}
                testID={testID}>
                <CheckboxIndicator>
                  <CheckboxIcon as={CheckIcon} />
                </CheckboxIndicator>

                <CheckboxLabel className={labelClassName}>{label}</CheckboxLabel>
              </Checkbox>

              {showError && (
                <FormControlError className="mt-2">
                  <FormControlErrorIcon />
                  <FormControlErrorText size="xs">
                    {String(fieldState.error?.message || '')}
                  </FormControlErrorText>
                </FormControlError>
              )}
            </FormControl>
          );
        }}
      />
    );
  }

  if (variant === 'phi' || variant === 'research') {
    const wrapClassName =
      variant === 'research'
        ? 'items-start rounded-xl border border-outline-500 bg-background-50 px-3 py-3'
        : 'items-start rounded-md bg-background-100 p-3';

    return <VStack className={twJoin(wrapClassName, className)}>{content}</VStack>;
  }

  return content;
}
